import React, { useContext, useEffect } from 'react';
import { useLocation, Outlet } from 'react-router-dom';

import ExpiringFundsBanner from 'components/common/ExpiringFundsBanner';
import SearchBanner from 'components/common/SearchBannerWithGraphic';

import {
  FEATURE_FLAGS,
  EXPIRING_FUNDS_ON_CONTROL_BANNER_SEGMENTS,
} from 'constants/featureFlag';
import CurrentUserContext from 'contexts/CurrentUserContext';
import FeatureFlagContext from 'contexts/FeatureFlagContext';

import { getExpiringFundsTotalAndDays, getWalletsExpiring } from 'utils/wallet';

import AccountBalanceBanner from './AccountBalanceBanner';
import { BANNER_TYPE } from 'constants/products';

import * as types from './types';

const SEARCH_PATH = '/search';

const StoreWithBannerLayout = ({
  children,
}: {
  children?: React.ReactNode;
}) => {
  const feature_flag = useContext(FeatureFlagContext);
  const location = useLocation();

  const expiring_funds_flag_v2 = feature_flag.flag(
    FEATURE_FLAGS.EXPIRING_FUNDS_BANNER_V2,
    'off'
  );
  const store_landing_flag = feature_flag.flag(
    FEATURE_FLAGS.STORE_LANDING_PAGE,
    'off'
  );

  const { employee } = useContext(CurrentUserContext);
  const initial_search_query =
    new URLSearchParams(location.search).get('q') ?? '';

  const [search_query, setSearchQuery] = React.useState(initial_search_query);
  useEffect(() => {
    if (location.pathname !== SEARCH_PATH) {
      setSearchQuery('');
    }
  }, [location.pathname]);

  const {
    settings: { currency },
  } = employee;

  const employee_wallets =
    employee?.employee_wallets.filter(
      ({
        company_wallet_configuration: { is_twic_store_enabled },
      }: types.EmployeeWallet) => is_twic_store_enabled
    ) || [];

  const wallets_expiring = getWalletsExpiring(employee_wallets);

  const { expiring_funds_days_left, expiring_funds_total } =
    getExpiringFundsTotalAndDays(wallets_expiring);

  const total_wallet_amount = employee_wallets
    .filter((ew) => ew.visible_to_employee)
    .reduce((total, { amount }) => total + amount, 0);
  const total_accounts =
    employee_wallets.filter((ew) => ew.visible_to_employee && ew.amount > 0)
      .length ?? 0;

  const show_expiring_funds_banner =
    expiring_funds_flag_v2 &&
    EXPIRING_FUNDS_ON_CONTROL_BANNER_SEGMENTS.includes(
      expiring_funds_flag_v2
    ) &&
    expiring_funds_total > 0 &&
    expiring_funds_days_left <= 10;

  const show_search_banner =
    employee.settings.is_posttax_enabled && employee_wallets.length > 0;

  return (
    <>
      {show_expiring_funds_banner && (
        <ExpiringFundsBanner
          currency={currency}
          days_left={expiring_funds_days_left}
          total={expiring_funds_total}
          wallets_expiring={wallets_expiring}
          variation={expiring_funds_flag_v2}
          banner_type={BANNER_TYPE.STORE}
        />
      )}
      {!show_expiring_funds_banner && store_landing_flag === 'landing' && (
        <AccountBalanceBanner
          amount={total_wallet_amount}
          num_of_accounts={total_accounts}
          currency={currency}
        />
      )}
      {show_search_banner && (
        <SearchBanner query={search_query} setSearchQuery={setSearchQuery} />
      )}
      {children ?? <Outlet />}
    </>
  );
};

export default StoreWithBannerLayout;
